import { useState, useCallback, useEffect } from "react";

// crypto.randomUUID() is only available in secure contexts (HTTPS).
// Fall back to Math.random for local/HTTP dev environments.
function genId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store/authStore";
import type {
  BuilderMode,
  FixedPagePolicy,
  PriceUnit,
  ProposalTemplate,
} from "../types";

export type WizardStep =
  | "template"
  | "client-info"
  | "project"
  | "review"
  | "done";

export type ProposalWizardMode = "create" | "edit";

export interface WizardAnswers {
  templateId: string | null;
  template: ProposalTemplate | null;

  templateBuilderMode: BuilderMode;
  templateFixedPagePolicy: FixedPagePolicy;
  templateStaticPageIds: string[];

  prospectName: string;
  prospectEmail: string;
  prospectAddress: string;
  prospectCity: string;
  prospectState: string;
  prospectZip: string;

  projectContext: string;
  additionalNotes: string;

  lineItems: WizardLineItem[];
  projectSummary: string;
  taxRate: number;
}

export interface WizardLineItem {
  _localId: string;
  service_item_id: string | null;
  name: string;
  description: string;
  price: number | null;
  price_unit: PriceUnit | null;
  frequency: string | null;
  duration_hours: number | null;
  sort_order: number;
  isGenerating: boolean;
}

export interface WizardState {
  currentStep: WizardStep;
  answers: WizardAnswers;
  proposalId: string | null;
  isCreating: boolean;
  isGenerating: boolean;
  error: string | null;
}

export interface EditableProposalSeed {
  id: string;
  client_id: string;
  template_id: string | null;
  prospect_name: string;
  prospect_email: string | null;
  prospect_address: string | null;
  prospect_city: string | null;
  prospect_state: string | null;
  prospect_zip: string | null;
  project_summary: string | null;
  ai_project_summary: string | null;
  notes: string | null;
  tax_rate: number | null;
  proposal_line_items?: Array<{
    id?: string;
    service_item_id: string | null;
    name: string;
    description: string | null;
    price: number | null;
    price_unit: PriceUnit | null;
    frequency: string | null;
    duration_hours: number | null;
    sort_order: number | null;
  }> | null;
}

export interface ProposalWizardOptions {
  mode?: ProposalWizardMode;
  initialProposal?: EditableProposalSeed | null;
  initialTemplate?: ProposalTemplate | null;
  onComplete?: (proposalId: string) => void;
}

function getTemplateBuilderMode(
  template: ProposalTemplate | null | undefined
): BuilderMode {
  return template?.builder_mode === "drag_drop" ? "drag_drop" : "prebuilt";
}

function getTemplateFixedPagePolicy(
  template: ProposalTemplate | null | undefined
): FixedPagePolicy {
  if (
    template?.fixed_page_policy === "brand_defined" ||
    template?.fixed_page_policy === "mixed"
  ) {
    return template.fixed_page_policy;
  }

  return "template_defined";
}

export function getAgentMessage(
  step: WizardStep,
  answers: WizardAnswers,
  mode: ProposalWizardMode = "create"
): {
  heading: string;
  body: string;
} {
  const clientType = answers.template?.client_type ?? answers.template?.category;
  const name = answers.prospectName;
  const templateName = answers.template?.name;
  const builderMode = answers.templateBuilderMode;
  const fixedPagePolicy = answers.templateFixedPagePolicy;

  switch (step) {
    case "template":
      return {
        heading:
          mode === "edit"
            ? "Let's update your proposal."
            : "Let's build your proposal.",
        body:
          "Start by choosing a template. Templates define the default services, pages, builder mode, and language direction for a type of client or job. Pick the one that best matches this proposal.",
      };

    case "client-info":
      return {
        heading: templateName
          ? `Great — using your ${templateName} template.`
          : "Good choice.",
        body:
          builderMode === "drag_drop"
            ? "Now tell me about the client. This proposal is starting from a drag-and-drop-ready template foundation, but your client details still drive the proposal header, cover language, and final scope."
            : "Now tell me about the client. I'll use this to personalise the proposal header and cover letter.",
      };

    case "project":
      return {
        heading: name ? `Got it — this proposal is for ${name}.` : "Almost there.",
        body:
          clientType === "construction"
            ? "Tell me about the construction project. What type of clean is this — final clean, progress clean, or something else? Any specifics I should know about the site?"
            : clientType === "restaurant"
              ? "Describe the restaurant or venue. What areas need to be covered — front of house, back of house, kitchen? How often does the client want service?"
              : clientType === "commercial"
                ? "Tell me about the commercial space. What type of building is it, and what's the scope — full building, common areas only, specific floors?"
                : clientType === "residential"
                  ? "Describe the home or property. Is this a recurring clean, a move-in/move-out, or a one-time deep clean?"
                  : "Describe the project in plain English. The more detail you give me, the better the result will be.",
      };

    case "review":
      return {
        heading:
          mode === "edit"
            ? "Review your updated proposal."
            : "Here's what I've put together.",
        body:
          fixedPagePolicy === "brand_defined" || fixedPagePolicy === "mixed"
            ? "I've drafted scope descriptions and a project summary based on your template, project details, and fixed-page-aware template defaults. Review everything below — edit any field before you finalise."
            : "I've drafted scope descriptions and a project summary based on your template and project details. Review everything below — click any field to edit it before you finalise.",
      };

    case "done":
      return {
        heading:
          mode === "edit"
            ? "Your proposal has been updated."
            : "Your proposal is ready.",
        body:
          mode === "edit"
            ? "Your changes are saved. You can keep editing, preview the PDF, or send it when you're ready."
            : "It's saved as a draft. You can keep editing, preview the PDF, or send it when you're ready.",
      };
  }
}

function initialAnswers(): WizardAnswers {
  return {
    templateId: null,
    template: null,
    templateBuilderMode: "prebuilt",
    templateFixedPagePolicy: "template_defined",
    templateStaticPageIds: [],
    prospectName: "",
    prospectEmail: "",
    prospectAddress: "",
    prospectCity: "",
    prospectState: "",
    prospectZip: "",
    projectContext: "",
    additionalNotes: "",
    lineItems: [],
    projectSummary: "",
    taxRate: 0,
  };
}

function legacyTemplateItemToWizardItem(
  item: ProposalTemplate["default_line_items"][number],
  idx: number
): WizardLineItem {
  return {
    _localId: genId(),
    service_item_id: item.service_item_id,
    name: item.name,
    description: item.description ?? "",
    price: item.price,
    price_unit: item.price_unit ?? null,
    frequency: item.frequency,
    duration_hours: item.duration_hours,
    sort_order: item.sort_order ?? idx,
    isGenerating: false,
  };
}

function relationalTemplateItemToWizardItem(
  item: NonNullable<ProposalTemplate["template_services"]>[number],
  idx: number
): WizardLineItem {
  return {
    _localId: genId(),
    service_item_id: item.service_item_id ?? null,
    name: item.service_name,
    description: item.service_description ?? "",
    price: item.base_price ?? null,
    price_unit: item.price_unit ?? null,
    frequency: null,
    duration_hours: null,
    sort_order: item.sort_order ?? idx,
    isGenerating: false,
  };
}

function proposalLineItemToWizardItem(
  item: NonNullable<EditableProposalSeed["proposal_line_items"]>[number],
  idx: number
): WizardLineItem {
  return {
    _localId: genId(),
    service_item_id: item.service_item_id,
    name: item.name,
    description: item.description ?? "",
    price: item.price,
    price_unit: item.price_unit ?? null,
    frequency: item.frequency,
    duration_hours: item.duration_hours,
    sort_order: item.sort_order ?? idx,
    isGenerating: false,
  };
}

function buildWizardLineItemsFromTemplate(
  template: ProposalTemplate
): WizardLineItem[] {
  if (
    Array.isArray(template.template_services) &&
    template.template_services.length > 0
  ) {
    return template.template_services
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((item, idx) => relationalTemplateItemToWizardItem(item, idx));
  }

  return (template.default_line_items ?? [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((item, idx) => legacyTemplateItemToWizardItem(item, idx));
}

function buildWizardLineItemsFromProposal(
  proposal: EditableProposalSeed | null | undefined
): WizardLineItem[] {
  if (!proposal?.proposal_line_items?.length) return [];

  return proposal.proposal_line_items
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((item, idx) => proposalLineItemToWizardItem(item, idx));
}

function getTemplateTaxRate(template: ProposalTemplate): number {
  if (typeof template.tax_rate === "number") return template.tax_rate;
  if (typeof template.default_tax_rate === "number") return template.default_tax_rate;
  return 0;
}

function getTemplateStaticPageIds(template: ProposalTemplate): string[] {
  if (
    Array.isArray(template.template_static_pages) &&
    template.template_static_pages.length > 0
  ) {
    return template.template_static_pages
      .slice()
      .sort((a, b) => (a.page_order ?? 0) - (b.page_order ?? 0))
      .map((page) => page.static_page_id)
      .filter(
        (pageId): pageId is string =>
          typeof pageId === "string" && pageId.length > 0
      );
  }

  return template.default_static_page_ids ?? [];
}

export function useProposalWizard(options?: ProposalWizardOptions) {
  const mode = options?.mode ?? "create";
  const initialProposal = options?.initialProposal ?? null;
  const initialTemplate = options?.initialTemplate ?? null;
  const onComplete = options?.onComplete;

  const clientId = useAuthStore((s) => s.clientId);
  const userId = useAuthStore((s) => s.userId);
  const qc = useQueryClient();

  const [state, setState] = useState<WizardState>({
    currentStep: mode === "edit" ? "review" : "template",
    answers: initialAnswers(),
    proposalId: mode === "edit" ? initialProposal?.id ?? null : null,
    isCreating: false,
    isGenerating: false,
    error: null,
  });

  useEffect(() => {
    if (mode !== "edit" || !initialProposal) return;

    setState((s) => ({
      ...s,
      currentStep: "review",
      proposalId: initialProposal.id,
      error: null,
      answers: {
        templateId: initialProposal.template_id,
        template: initialTemplate,
        templateBuilderMode: getTemplateBuilderMode(initialTemplate),
        templateFixedPagePolicy: getTemplateFixedPagePolicy(initialTemplate),
        templateStaticPageIds: initialTemplate
          ? getTemplateStaticPageIds(initialTemplate)
          : [],
        prospectName: initialProposal.prospect_name ?? "",
        prospectEmail: initialProposal.prospect_email ?? "",
        prospectAddress: initialProposal.prospect_address ?? "",
        prospectCity: initialProposal.prospect_city ?? "",
        prospectState: initialProposal.prospect_state ?? "",
        prospectZip: initialProposal.prospect_zip ?? "",
        projectContext:
          initialProposal.project_summary ??
          initialProposal.ai_project_summary ??
          "",
        additionalNotes: initialProposal.notes ?? "",
        lineItems: buildWizardLineItemsFromProposal(initialProposal),
        projectSummary:
          initialProposal.ai_project_summary ??
          initialProposal.project_summary ??
          "",
        taxRate: initialProposal.tax_rate ?? 0,
      },
    }));
  }, [initialProposal, initialTemplate, mode]);

  const setStep = useCallback((step: WizardStep) => {
    setState((s) => ({ ...s, currentStep: step, error: null }));
    window.scrollTo(0, 0);
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((s) => ({
      ...s,
      error,
      isCreating: false,
      isGenerating: false,
    }));
  }, []);

  const updateAnswers = useCallback((updates: Partial<WizardAnswers>) => {
    setState((s) => ({
      ...s,
      answers: { ...s.answers, ...updates },
    }));
  }, []);

  const selectTemplate = useCallback((template: ProposalTemplate) => {
    const lineItems = buildWizardLineItemsFromTemplate(template);
    const templateStaticPageIds = getTemplateStaticPageIds(template);

    setState((s) => ({
      ...s,
      currentStep: "client-info",
      error: null,
      answers: {
        ...s.answers,
        templateId: template.id,
        template,
        templateBuilderMode: getTemplateBuilderMode(template),
        templateFixedPagePolicy: getTemplateFixedPagePolicy(template),
        templateStaticPageIds,
        taxRate: getTemplateTaxRate(template),
        lineItems,
      },
    }));

    window.scrollTo(0, 0);
  }, []);

  const submitClientInfo = useCallback(
    async (info: {
      prospectName: string;
      prospectEmail: string;
      prospectAddress: string;
      prospectCity: string;
      prospectState: string;
      prospectZip: string;
    }) => {
      if (!clientId) return;

      const currentAnswers = state.answers;
      const existingProposalId = state.proposalId;

      setState((s) => ({
        ...s,
        isCreating: true,
        error: null,
        answers: {
          ...s.answers,
          ...info,
        },
      }));

      try {
        if (mode === "edit" && existingProposalId) {
          const { error } = await supabase
            .from("proposals")
            .update({
              template_id: currentAnswers.templateId,
              prospect_name: info.prospectName,
              prospect_email: info.prospectEmail || null,
              prospect_address: info.prospectAddress || null,
              prospect_city: info.prospectCity || null,
              prospect_state: info.prospectState || null,
              prospect_zip: info.prospectZip || null,
              tax_rate: currentAnswers.taxRate,
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingProposalId)
            .eq("client_id", clientId);

          if (error) throw error;

          setState((s) => ({
            ...s,
            isCreating: false,
            currentStep: "project",
            error: null,
            answers: {
              ...s.answers,
              ...info,
            },
          }));
        } else {
          // Fetch template layout so each proposal gets its own independent copy
          let templateLayoutJson: unknown = null;
          if (currentAnswers.templateId) {
            const { data: tmpl } = await supabase
              .from("proposal_templates")
              .select("layout_json")
              .eq("id", currentAnswers.templateId)
              .single();
            templateLayoutJson = tmpl?.layout_json ?? null;
          }

          const { data: proposal, error } = await supabase
            .from("proposals")
            .insert({
              client_id: clientId,
              created_by: userId,
              template_id: currentAnswers.templateId,
              prospect_name: info.prospectName,
              prospect_email: info.prospectEmail || null,
              prospect_address: info.prospectAddress || null,
              prospect_city: info.prospectCity || null,
              prospect_state: info.prospectState || null,
              prospect_zip: info.prospectZip || null,
              status: "draft",
              tax_rate: currentAnswers.taxRate,
              layout_json: templateLayoutJson,
            })
            .select()
            .single();

          if (error) throw error;

          setState((s) => ({
            ...s,
            proposalId: proposal.id,
            isCreating: false,
            currentStep: "project",
            error: null,
            answers: {
              ...s.answers,
              ...info,
            },
          }));
        }

        window.scrollTo(0, 0);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to save proposal client info"
        );
      }
    },
    [clientId, mode, setError, state.answers, state.proposalId, userId]
  );

  const submitProjectContext = useCallback(
    async (context: {
      projectContext: string;
      additionalNotes: string;
    }) => {
      const currentState = state;
      const currentAnswers = currentState.answers;
      const template = currentAnswers.template;
      const proposalId = currentState.proposalId;

      setState((s) => ({
        ...s,
        isGenerating: true,
        error: null,
        answers: {
          ...s.answers,
          ...context,
        },
      }));

      try {
        const fullContext = [
          template?.ai_industry_context,
          template?.ai_service_context,
          context.projectContext,
          context.additionalNotes,
        ]
          .filter(Boolean)
          .join("\n\n");

        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error("Not authenticated");

        const lineItemsForAI = currentAnswers.lineItems.map((item, index) => ({
          index,
          name: item.name,
          existingDesc: item.description || undefined,
        }));

        const { data: scopeData, error: scopeErr } =
          await supabase.functions.invoke("ai-proposal-writer", {
            body: {
              action: "generate_scope_items",
              payload: {
                lineItems: lineItemsForAI,
                projectContext: fullContext,
                brandVoice: template?.ai_tone ?? "professional",
                proposalId: proposalId ?? undefined,
                templateId: template?.id ?? undefined,
                builderMode: currentAnswers.templateBuilderMode,
                fixedPagePolicy: currentAnswers.templateFixedPagePolicy,
              },
            },
            headers: {
              Authorization: `Bearer ${token}`,
              apikey: (import.meta.env as Record<string, string>)
                .VITE_SUPABASE_ANON_KEY,
            },
          });

        if (scopeErr) throw scopeErr;

        const { data: summaryData, error: summaryErr } =
          await supabase.functions.invoke("ai-proposal-writer", {
            body: {
              action: "polish_project_summary",
              payload: {
                rawSummary: context.projectContext,
                proposalId: proposalId ?? undefined,
                builderMode: currentAnswers.templateBuilderMode,
                fixedPagePolicy: currentAnswers.templateFixedPagePolicy,
              },
            },
            headers: {
              Authorization: `Bearer ${token}`,
              apikey: (import.meta.env as Record<string, string>)
                .VITE_SUPABASE_ANON_KEY,
            },
          });

        if (summaryErr) throw summaryErr;

        const aiLineItems = scopeData?.data?.line_items ?? [];
        const updatedLineItems = currentAnswers.lineItems.map((item, idx) => ({
          ...item,
          description: aiLineItems[idx]?.description ?? item.description,
        }));

        const polishedSummary =
          summaryData?.data?.polished_summary ?? context.projectContext;

        setState((s) => ({
          ...s,
          isGenerating: false,
          currentStep: "review",
          error: null,
          answers: {
            ...s.answers,
            ...context,
            lineItems: updatedLineItems,
            projectSummary: polishedSummary,
          },
        }));

        window.scrollTo(0, 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : "AI generation failed");
      }
    },
    [setError, state]
  );

  const updateLineItem = useCallback(
    (localId: string, updates: Partial<WizardLineItem>) => {
      setState((s) => ({
        ...s,
        answers: {
          ...s.answers,
          lineItems: s.answers.lineItems.map((item) =>
            item._localId === localId ? { ...item, ...updates } : item
          ),
        },
      }));
    },
    []
  );

  const finalizeProposal = useCallback(async () => {
    if (!clientId) return;

    const currentState = state;
    const answers = currentState.answers;
    const proposalId = currentState.proposalId;

    setState((s) => ({ ...s, isCreating: true, error: null }));

    try {
      if (!proposalId) {
        throw new Error("Missing proposal ID");
      }

      const { error: updateErr } = await supabase
        .from("proposals")
        .update({
          template_id: answers.templateId,
          prospect_name: answers.prospectName,
          prospect_email: answers.prospectEmail || null,
          prospect_address: answers.prospectAddress || null,
          prospect_city: answers.prospectCity || null,
          prospect_state: answers.prospectState || null,
          prospect_zip: answers.prospectZip || null,
          project_summary: answers.projectContext,
          ai_project_summary: answers.projectSummary,
          notes: answers.additionalNotes || null,
          tax_rate: answers.taxRate,
          updated_at: new Date().toISOString(),
        })
        .eq("id", proposalId)
        .eq("client_id", clientId);

      if (updateErr) throw updateErr;

      const { error: deleteLineItemsErr } = await supabase
        .from("proposal_line_items")
        .delete()
        .eq("proposal_id", proposalId);

      if (deleteLineItemsErr) throw deleteLineItemsErr;

      const { error: deletePagesErr } = await supabase
        .from("proposal_static_pages")
        .delete()
        .eq("proposal_id", proposalId);

      if (deletePagesErr) throw deletePagesErr;

      if (answers.lineItems.length > 0) {
        const lineItemRows = answers.lineItems.map((item, idx) => ({
          proposal_id: proposalId,
          service_item_id: item.service_item_id,
          name: item.name,
          description: item.description || null,
          price: item.price ?? 0,
          price_unit: item.price_unit,
          frequency: item.frequency,
          duration_hours: item.duration_hours,
          sort_order: idx,
          quantity: 1,
        }));

        const { error: liErr } = await supabase
          .from("proposal_line_items")
          .insert(lineItemRows);

        if (liErr) throw liErr;
      }

      const staticPageIds = Array.from(
        new Set(
          answers.templateStaticPageIds.filter(
            (pageId): pageId is string =>
              typeof pageId === "string" && pageId.trim().length > 0
          )
        )
      );

      if (staticPageIds.length > 0) {
        const pageRows = staticPageIds.map((pageId, idx) => ({
          proposal_id: proposalId,
          static_page_id: pageId,
          page_order: idx,
        }));

        const { error: spErr } = await supabase
          .from("proposal_static_pages")
          .insert(pageRows);

        if (spErr) throw spErr;
      }

      qc.invalidateQueries({ queryKey: ["proposals", clientId, "list"] });
      qc.invalidateQueries({ queryKey: ["proposals", "detail", proposalId] });

      setState((s) => ({
        ...s,
        isCreating: false,
        currentStep: "done",
        error: null,
      }));

      if (onComplete) {
        onComplete(proposalId);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to save proposal"
      );
    }
  }, [clientId, onComplete, qc, setError, state]);

  const goBack = useCallback(() => {
    const order: WizardStep[] = ["template", "client-info", "project", "review"];
    const idx = order.indexOf(state.currentStep);
    if (idx > 0) setStep(order[idx - 1]);
  }, [setStep, state.currentStep]);

  return {
    mode,
    currentStep: state.currentStep,
    answers: state.answers,
    proposalId: state.proposalId,
    isCreating: state.isCreating,
    isGenerating: state.isGenerating,
    error: state.error,

    agentMessage: getAgentMessage(state.currentStep, state.answers, mode),

    selectTemplate,
    submitClientInfo,
    submitProjectContext,
    updateLineItem,
    updateAnswers,
    finalizeProposal,
    goBack,
    setStep,
  };
}